import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { useApiUrls } from "../useApiUrl";
import { http } from "../../utils/httpCommon";
import { queryClient } from "../../queryClientProvider";
import { safeParsePartial } from "../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import { deliveryRouteSequenceStatusSchema } from "../../types/planning/plan.type";

const deliveryRouteSequenceStatusResponseSchema = z.array(
  deliveryRouteSequenceStatusSchema
);

export type DeliveryRouteSequenceStatus = z.infer<
  typeof deliveryRouteSequenceStatusSchema
>;

const checkDeliveryRouteSequenceStatusRefetchInterval = 4000; // 4s

type UseDeliveryRoutesSequenceStatusProps = {
  routeIds: number[];
  timestamp: number | null;
  onAutoSequenceRoutesFinished: (result: DeliveryRouteSequenceStatus[]) => void;
};

const keys = {
  getDeliveryRoutesSequenceStatus: (
    routeIds: number[],
    processId: number | null
  ) => ["delivery-routes", "sequence-status", routeIds, processId],
};

export const useDeliveryRoutesSequenceStatus = ({
  onAutoSequenceRoutesFinished,
  timestamp: processId,
  routeIds,
}: UseDeliveryRoutesSequenceStatusProps) => {
  const countRef = useRef(0);
  const previousProcessIdRef = useRef<number | null>(null);
  const hasFinishedRef = useRef(false);
  const hookUrls = useApiUrls();
  const { getDeliveryRoutesSequenceStatus } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  // Reset counter and state when a new auto-sequence operation starts (processId changes)
  useEffect(() => {
    if (
      processId !== null &&
      processId !== 0 &&
      processId !== previousProcessIdRef.current
    ) {
      // Cancel and remove ALL previous sequence-status queries before starting new one
      queryClient.cancelQueries({
        queryKey: ["delivery-routes", "sequence-status"],
        exact: false,
      });

      queryClient.removeQueries({
        queryKey: ["delivery-routes", "sequence-status"],
        exact: false,
      });

      // Reset counters for new operation
      countRef.current = 0;
      hasFinishedRef.current = false;
      previousProcessIdRef.current = processId;
    }
  }, [processId]);

  // Reset counter when route IDs change (new routes being sequenced)
  useEffect(() => {
    if (routeIds.length === 0) {
      countRef.current = 0;
      hasFinishedRef.current = false;
    }
  }, [routeIds.length]);

  const { data: sequenceStatus = [] } = useQuery({
    // Only enable polling when we have route IDs AND the timestamp is set (not initial value)
    // This prevents polling from starting before the auto-sequence API is called
    enabled:
      routeIds.length > 0 &&
      !hasFinishedRef.current &&
      processId !== null &&
      processId !== 0,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.getDeliveryRoutesSequenceStatus(routeIds, processId),
    queryFn: async () => {
      const { data } = await http.get(
        getDeliveryRoutesSequenceStatus(routeIds)
      );
      countRef.current += 1;
      return isSafeParseEnabled
        ? safeParsePartial(
            deliveryRouteSequenceStatusResponseSchema,
            data,
            "useDeliveryRoutesSequenceStatus"
          )
        : deliveryRouteSequenceStatusResponseSchema.parse(data);
    },
    refetchInterval: () => {
      // Stop polling if finished or exceeded timeout
      if (hasFinishedRef.current || countRef.current >= 75) {
        return false;
      }
      return checkDeliveryRouteSequenceStatusRefetchInterval;
    },
  });

  const isAutoSequenceFinished =
    sequenceStatus.length > 0 &&
    sequenceStatus.every((route) => route.sequenceStatus !== "processing");

  // Use useEffect to handle the finished state to avoid calling setState during render
  useEffect(() => {
    if (isAutoSequenceFinished && !hasFinishedRef.current) {
      // Immediately mark as finished to stop polling
      hasFinishedRef.current = true;

      // Clear the polling query data immediately to stop any pending requests
      queryClient.cancelQueries({
        queryKey: ["delivery-routes", "sequence-status"],
        exact: false,
      });

      queryClient.removeQueries({
        queryKey: ["delivery-routes", "sequence-status"],
        exact: false,
      });

      // Invalidate trailer-shipments queries for processed routes
      sequenceStatus.forEach((sequencedRoute) => {
        if (sequencedRoute.sequenceStatus === "processed") {
          queryClient.invalidateQueries({
            queryKey: [
              "planning",
              sequencedRoute.id,
              "delivery_route",
              "trailer-shipments",
            ],
          });
        }
      });

      // Call the callback which will refetch plans and shipments
      onAutoSequenceRoutesFinished(sequenceStatus);
    }
  }, [isAutoSequenceFinished, sequenceStatus, onAutoSequenceRoutesFinished]);

  return {
    isAutoSequenceRunning: routeIds.length > 0,
  };
};
